import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { useCallback, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { logError } from 'utils/monitoring';

// Hooks
import { useModal } from 'components/modals/useModal';

// Util
import { errorAlert, successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';

// Components
import CheckboxListInput from 'components/forms/inputs/rhf-inputs/CheckboxListInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { ModalFooterSimple } from 'components/modals/ModalFooterSimple';
import { ModalHeader } from 'components/modals/ModalHeader';

// Store
import { useUpdateBulkInvoiceDebtNotesMutation } from 'store/api/invoices';
import { RootState } from 'store/reducers';
import { clearDebtReminderInvoices } from 'store/reducers/invoices';

// Types
import { BulkDebtNotesUpdateDto, InvoiceDataListItem } from 'types/invoices';

interface BulkDebtNotesModalProps {
    onClose: () => void;
    invoices: InvoiceDataListItem[];
}

interface BulkDebtNotesForm {
    debtNotes: string;
    selectedInvoiceIds: number[];
    nextReminderDate?: string | null;
}

const BulkDebtNotesModal = (props: BulkDebtNotesModalProps) => {
    const { onClose, invoices } = props;
    const dispatch = useDispatch();
    const buttonsDisabled = useSelector((state: RootState) => state.buttons.buttonsDisabled);
    const [updateBulkInvoiceDebtNotes] = useUpdateBulkInvoiceDebtNotesMutation();

    // Sort invoices alphabetically by invoice number
    const sortedInvoices = useMemo(() => {
        return invoices.toSorted((a, b) => a.invoiceNumber.localeCompare(b.invoiceNumber));
    }, [invoices]);

    const invoiceOptions = useMemo(() => {
        return sortedInvoices.map((invoice) => ({
            value: invoice.id,
            label: invoice.invoiceNumber,
        }));
    }, [sortedInvoices]);

    const defaultSelectedIds = useMemo(() => {
        return sortedInvoices.map((invoice) => invoice.id);
    }, [sortedInvoices]);

    const { handleSubmit, control } = useForm<BulkDebtNotesForm>({
        defaultValues: {
            debtNotes: '',
            selectedInvoiceIds: defaultSelectedIds,
            nextReminderDate: null,
        },
    });

    const [selectedInvoiceIds] = useWatch({
        control,
        name: ['selectedInvoiceIds'],
    });

    const isSaveDisabled = selectedInvoiceIds.length === 0;

    const handleClose = useCallback(() => {
        dispatch(clearDebtReminderInvoices());
        onClose();
    }, [dispatch, onClose]);

    const onSubmit = useCallback(
        async (data: BulkDebtNotesForm) => {
            const payload: BulkDebtNotesUpdateDto = {
                invoiceIds: data.selectedInvoiceIds,
                debtNotes: data.debtNotes?.trim() || null,
                nextReminderDate: data.nextReminderDate || null,
            };

            try {
                await updateBulkInvoiceDebtNotes(payload).unwrap();
                successAlert(t('invoices.bulk-debt-notes-success'));
                dispatch(clearDebtReminderInvoices());
                onClose();
            } catch (error) {
                console.error('Failed to update bulk debt notes:', error);
                errorAlert(t('invoices.bulk-debt-notes-error'));
                logError(error);
            }
        },
        [updateBulkInvoiceDebtNotes, dispatch, onClose]
    );

    return (
        <>
            <ModalHeader title={t('invoices.add-debt-note-title')} onCancel={handleClose} />
            <div className="h-full overflow-y-auto p-6 max-sm:px-4">
                <div className="flex flex-col gap-6">
                    <TextAreaInput
                        control={control}
                        label="invoices.debt-collection-note-text"
                        placeholder="invoices.add-debt-note"
                        name="debtNotes"
                        minRows={10}
                        enableAutofill
                    />

                    <DateInput
                        control={control}
                        name="nextReminderDate"
                        label="invoices.next-reminder-date"
                        placeholder="forms.select-date"
                        fullWidth
                    />

                    <div className="flex flex-col gap-2">
                        <div>{t('invoices.bulk-debt-notes.list-intro')}</div>
                        <CheckboxListInput
                            control={control}
                            name="selectedInvoiceIds"
                            label=""
                            options={invoiceOptions}
                        />
                    </div>
                </div>
            </div>
            <ModalFooterSimple
                buttons={[
                    <Button
                        {...buttonStyle({ variant: 'outlined', color: 'primary' })}
                        key="cancel"
                        onClick={handleClose}
                    >
                        {t('cancel')}
                    </Button>,
                    <Button
                        {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                        key="save"
                        onClick={() => {
                            void handleSubmit(onSubmit)();
                        }}
                        disabled={buttonsDisabled || isSaveDisabled}
                    >
                        {t('save')}
                    </Button>,
                ]}
            />
        </>
    );
};

export const useBulkDebtNotesModal = (invoices: InvoiceDataListItem[] | undefined) => {
    const { openModal, closeModal } = useModal();

    const openBulkDebtNotesModal = useCallback(() => {
        if (Array.isArray(invoices) && invoices.length > 0) {
            openModal(<BulkDebtNotesModal onClose={closeModal} invoices={invoices} />, closeModal);
        }
    }, [invoices, openModal, closeModal]);

    return {
        openBulkDebtNotesModal,
    };
};
